import type {
    DataSourceRequestState,
    SortDescriptor
} from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import {
    GridColumn,
    GridToolbar,
    type GridCellProps,
    type GridCustomCellProps,
    type GridDataStateChangeEvent,
    type GridPageChangeEvent,
    type GridSortChangeEvent
} from "@progress/kendo-react-grid";
import { Input } from "@progress/kendo-react-inputs";
import { useEffect, useState } from "react";
import { useListCustomersQuery } from "../../api/admin/adminCustomerApi";
import {
    useCreateUserMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useLazyListUsersQuery
} from "../../api/admin/adminUserApi";
import AppGrid from "../../components/grid/appGrid";
import { AppRoles } from "../../constants/appRoles";
import type { CustomerDto, UserDto } from "../../contracts";

const roles: string[] = Object.values(AppRoles);
const customerModes = ["Existing customer", "New customer"];
const initialSort: SortDescriptor[] = [{ field: "userName", dir: "asc" }];
const passwordRequirements
    = "Use at least 8 characters with a lowercase letter, an uppercase letter, a digit, and a non-alphanumeric character.";

type CreateUserField
    = | "firstName"
        | "lastName"
        | "email"
        | "password"
        | "confirmPassword"
        | "roles"
        | "customer";

const validateName = (value: string, label: string): string | null => {
    if (value.trim() === "") {
        return `${label} is required.`;
    }

    return value.trim().length > 100 ? `${label} must be at most 100 characters.` : null;
};

const validateEmail = (value: string): string | null => {
    const trimmedValue = value.trim();

    if (trimmedValue === "") {
        return "Email is required.";
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) {
        return "Enter a valid email address.";
    }

    return null;
};

const validatePassword = (value: string): string | null => {
    if (value === "") {
        return "Password is required.";
    }

    const meetsRequirements
        = value.length >= 8
            && /[0-9]/.test(value)
            && /[a-z]/.test(value)
            && /[A-Z]/.test(value)
            && /[^a-zA-Z0-9]/.test(value);

    return meetsRequirements ? null : passwordRequirements;
};

const validateConfirmPassword = (
    value: string,
    password: string
): string | null => {
    if (value === "") {
        return "Confirm password is required.";
    }

    return value === password ? null : "Passwords do not match.";
};

const RolesDataCell = (props: GridCellProps) => {
    const roles = Array.isArray(props.dataItem.roles) ? props.dataItem.roles : [];
    return (
        <td className="k-table-td">{roles.length > 0 ? roles.join(", ") : "-"}</td>
    );
};

const UserManagement: React.FC = () => {
    const [triggerListUsersQuery, { data, isFetching }] = useLazyListUsersQuery();
    const {
        data: customersData,
        isFetching: isFetchingCustomers,
        refetch: refetchCustomers
    } = useListCustomersQuery({ take: 1000 });
    const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
    const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
    const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
    const [editingUser, setEditingUser] = useState<UserDto | null>(null);
    const [editFirstName, setEditFirstName] = useState("");
    const [editLastName, setEditLastName] = useState("");
    const [editError, setEditError] = useState<string | null>(null);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [selectedRoles, setSelectedRoles] = useState<string[]>(["User"]);
    const [customerMode, setCustomerMode] = useState(customerModes[0]);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerDto | null>(
        null
    );
    const [customerName, setCustomerName] = useState("");
    const [touchedFields, setTouchedFields] = useState<
        Partial<Record<CreateUserField, boolean>>
    >({});
    const [dataState, setDataState] = useState<DataSourceRequestState>({
        skip: 0,
        take: 20,
        sort: initialSort
    });

    useEffect(() => {
        triggerListUsersQuery({
            ...dataState
        });
    }, [triggerListUsersQuery, dataState]);

    const effectiveSelectedCustomer
        = selectedCustomer ?? customersData?.data[0] ?? null;
    const firstNameValidationMessage = validateName(firstName, "First name");
    const lastNameValidationMessage = validateName(lastName, "Last name");
    const editFirstNameValidation = validateName(editFirstName, "First name");
    const editLastNameValidation = validateName(editLastName, "Last name");
    const emailValidationMessage = validateEmail(email);
    const passwordValidationMessage = validatePassword(password);
    const confirmPasswordValidationMessage = validateConfirmPassword(
        confirmPassword,
        password
    );
    const rolesValidationMessage
        = selectedRoles.length > 0 ? null : "Select at least one role.";
    const customerValidationMessage
        = customerMode === customerModes[0]
            ? effectiveSelectedCustomer == null
                ? "Select a customer."
                : null
            : customerName.trim() === ""
                ? "Customer name is required."
                : null;
    const canCreate
        = firstNameValidationMessage == null
            && lastNameValidationMessage == null
            && emailValidationMessage == null
            && passwordValidationMessage == null
            && confirmPasswordValidationMessage == null
            && rolesValidationMessage == null
            && customerValidationMessage == null;

    const touchField = (field: CreateUserField): void => {
        setTouchedFields(previous => ({ ...previous, [field]: true }));
    };

    const refreshUsers = () => {
        triggerListUsersQuery({
            ...dataState
        });
    };

    const pageChange = (event: GridPageChangeEvent) => {
        setDataState(prev => ({
            ...prev,
            skip: event.page.skip,
            take: event.page.take
        }));
    };

    const dataStateChange = (event: GridDataStateChangeEvent) => {
        setDataState({
            ...event.dataState,
            skip: event.dataState.skip,
            take: event.dataState.take
        });
    };

    const sortChange = (event: GridSortChangeEvent) => {
        setDataState(prev => ({
            ...prev,
            sort: event.sort
        }));
    };

    const resetCreateForm = () => {
        setFirstName("");
        setLastName("");
        setEmail("");
        setPhoneNumber("");
        setPassword("");
        setConfirmPassword("");
        setSelectedRoles(["User"]);
        setCustomerMode(customerModes[0]);
        setSelectedCustomer(customersData?.data[0] ?? null);
        setCustomerName("");
        setTouchedFields({});
    };

    const handleCreateAsync = async (): Promise<void> => {
        if (!canCreate) {
            setTouchedFields({
                firstName: true,
                lastName: true,
                email: true,
                password: true,
                confirmPassword: true,
                roles: true,
                customer: true
            });
            return;
        }

        try {
            await createUser({
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                email: email.trim(),
                phone_number: phoneNumber.trim(),
                password,
                confirm_password: confirmPassword,
                roles: selectedRoles,
                customer_id:
                    customerMode === customerModes[0]
                        ? (effectiveSelectedCustomer?.id ?? null)
                        : null,
                customer_name:
                    customerMode === customerModes[1] ? customerName.trim() : null
            }).unwrap();

            setErrorMessage(null);
            setIsCreateDialogOpen(false);
            resetCreateForm();
            refetchCustomers();
            refreshUsers();
        } catch {
            setErrorMessage("Unable to create user.");
        }
    };

    const openEditUser = (user: UserDto) => {
        setEditingUser(user);
        setEditFirstName(user.first_name ?? "");
        setEditLastName(user.last_name ?? "");
        setEditError(null);
    };

    const handleUpdateAsync = async (): Promise<void> => {
        if (!editingUser || editFirstNameValidation || editLastNameValidation) {
            return;
        }

        try {
            await updateUser({
                user_id: editingUser.id,
                first_name: editFirstName.trim(),
                last_name: editLastName.trim()
            }).unwrap();
            setEditingUser(null);
            setEditError(null);
            refreshUsers();
        } catch {
            setEditError("Unable to update user names.");
        }
    };

    const handleDeleteAsync = async (
        userId: string,
        userName: string
    ): Promise<void> => {
        const isConfirmed = window.confirm(
            `Are you sure you want to delete ${userName}?`
        );
        if (!isConfirmed) {
            return;
        }

        try {
            const response = await deleteUser({ user_id: userId }).unwrap();
            if (response.is_success) {
                setErrorMessage(null);
                refreshUsers();
                return;
            }

            setErrorMessage(response.error_message ?? "Unable to delete user.");
        } catch {
            setErrorMessage("Unable to delete user.");
        }
    };

    const DeleteActionCell = (props: GridCustomCellProps) => (
        <td
            {...props.tdProps}
            className={`${props.tdProps?.className ?? ""} text-center align-middle`}
        >
            <Button
                type="button"
                fillMode="outline"
                size="small"
                className="me-2"
                disabled={isDeleting || isFetching || isUpdating}
                onClick={() => openEditUser(props.dataItem as UserDto)}
            >
                Edit
            </Button>
            <Button
                type="button"
                themeColor="error"
                fillMode="outline"
                size="small"
                disabled={isDeleting || isFetching || isUpdating}
                onClick={() =>
                    void handleDeleteAsync(
                        props.dataItem.id as string,
                        props.dataItem.user_name as string
                    )}
            >
                Delete
            </Button>
        </td>
    );

    const showCustomerValidationMessage
        = touchedFields.customer === true && customerValidationMessage != null;
    const customerInput
        = customerMode === customerModes[0]
            ? (
                    <>
                        <DropDownList
                            label="Customer"
                            data={customersData?.data ?? []}
                            textField="name"
                            dataItemKey="id"
                            value={effectiveSelectedCustomer}
                            loading={isFetchingCustomers}
                            valid={!showCustomerValidationMessage}
                            validationMessage={customerValidationMessage ?? undefined}
                            onChange={event => setSelectedCustomer(event.value)}
                            onBlur={() => touchField("customer")}
                        />
                        {showCustomerValidationMessage && (
                            <div className="text-danger small">{customerValidationMessage}</div>
                        )}
                    </>
                )
            : (
                    <>
                        <Input
                            label="New Customer Name"
                            value={customerName}
                            valid={!showCustomerValidationMessage}
                            validationMessage={customerValidationMessage ?? undefined}
                            onChange={event => setCustomerName(String(event.value ?? ""))}
                            onBlur={() => touchField("customer")}
                        />
                        {showCustomerValidationMessage && (
                            <div className="text-danger small">{customerValidationMessage}</div>
                        )}
                    </>
                );

    return (
        <div className="container-fluid py-3 d-flex flex-column h-100 min-h-0 overflow-hidden">
            <div className="d-flex flex-column gap-2 mb-3">
                <h2 className="h4 mb-0">User Management</h2>
                <p className="text-muted mb-0">
                    Application users from identity store.
                </p>
            </div>

            {errorMessage && (
                <div className="alert alert-danger" role="alert">
                    {errorMessage}
                </div>
            )}

            <div className="flex-grow-1 min-h-0 overflow-hidden">
                <AppGrid
                    scrollable="scrollable"
                    showLoader={isFetching || isDeleting || isCreating || isUpdating}
                    data={data?.data ?? []}
                    total={data?.total ?? 0}
                    pageable={{ buttonCount: 3, pageSizes: [20, 50, 100] }}
                    sortable
                    initialState={dataState}
                    skip={dataState.skip}
                    take={dataState.take}
                    sort={dataState.sort}
                    onPageChange={pageChange}
                    onDataStateChange={dataStateChange}
                    onSortChange={sortChange}
                    style={{
                        width: "100%",
                        height: "100%"
                    }}
                >
                    <GridToolbar>
                        <Button
                            type="button"
                            themeColor="primary"
                            onClick={() => setIsCreateDialogOpen(true)}
                        >
                            Create User
                        </Button>
                        <Button type="button" fillMode="outline" onClick={refreshUsers}>
                            Refresh
                        </Button>
                    </GridToolbar>
                    <GridColumn field="first_name" title="First Name" width="180px" />
                    <GridColumn field="last_name" title="Last Name" width="180px" />
                    <GridColumn field="email" title="Email" width="320px" />
                    <GridColumn
                        field="customer_name"
                        title="Customer"
                        width="220px"
                        sortable={false}
                    />
                    <GridColumn field="phone_number" title="Phone" width="180px" />
                    <GridColumn
                        field="roles"
                        title="Roles"
                        width="220px"
                        cells={{ data: RolesDataCell }}
                        sortable={false}
                    />
                    <GridColumn
                        field="email_confirmed"
                        title="Email Confirmed"
                        width="150px"
                    />
                    <GridColumn
                        field="phone_number_confirmed"
                        title="Phone Confirmed"
                        width="150px"
                    />
                    <GridColumn field="two_factor_enabled" title="2FA" width="110px" />
                    <GridColumn field="lockout_enabled" title="Lockout" width="110px" />
                    <GridColumn
                        field="access_failed_count"
                        title="Access Failed"
                        width="130px"
                    />
                    <GridColumn title="Actions" width="240px" locked={true} cells={{ data: DeleteActionCell }} />
                </AppGrid>
            </div>

            {editingUser && (
                <Dialog
                    title="Edit User"
                    width={500}
                    onClose={() => {
                        if (!isUpdating) setEditingUser(null);
                    }}
                >
                    <div className="d-flex flex-column gap-3">
                        {editError && <div className="alert alert-danger" role="alert">{editError}</div>}
                        <Input
                            label="First Name"
                            autoComplete="given-name"
                            value={editFirstName}
                            disabled={isUpdating}
                            valid={editFirstNameValidation == null}
                            validationMessage={editFirstNameValidation ?? undefined}
                            onChange={event => setEditFirstName(String(event.value ?? ""))}
                        />
                        {editFirstNameValidation && <div className="text-danger small">{editFirstNameValidation}</div>}
                        <Input
                            label="Last Name"
                            autoComplete="family-name"
                            value={editLastName}
                            disabled={isUpdating}
                            valid={editLastNameValidation == null}
                            validationMessage={editLastNameValidation ?? undefined}
                            onChange={event => setEditLastName(String(event.value ?? ""))}
                        />
                        {editLastNameValidation && <div className="text-danger small">{editLastNameValidation}</div>}
                    </div>
                    <DialogActionsBar>
                        <Button type="button" disabled={isUpdating} onClick={() => setEditingUser(null)}>
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            themeColor="primary"
                            disabled={isUpdating || editFirstNameValidation != null || editLastNameValidation != null}
                            onClick={() => void handleUpdateAsync()}
                        >
                            Save
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {isCreateDialogOpen && (
                <Dialog
                    title="Create User"
                    width="min(560px, calc(100vw - 2rem))"
                    height="min(900px, calc(100dvh - 2rem))"
                    contentStyle={{ overflowY: "auto", minHeight: 0 }}
                    onClose={() => setIsCreateDialogOpen(false)}
                >
                    <div className="d-flex flex-column gap-3">
                        <Input
                            id="firstName"
                            name="firstName"
                            label="First Name"
                            autoComplete="given-name"
                            value={firstName}
                            valid={touchedFields.firstName !== true || firstNameValidationMessage == null}
                            validationMessage={firstNameValidationMessage ?? undefined}
                            onChange={event => setFirstName(String(event.value ?? ""))}
                            onBlur={() => touchField("firstName")}
                        />
                        {touchedFields.firstName === true && firstNameValidationMessage != null && (
                            <div className="text-danger small">{firstNameValidationMessage}</div>
                        )}
                        <Input
                            id="lastName"
                            name="lastName"
                            label="Last Name"
                            autoComplete="family-name"
                            value={lastName}
                            valid={touchedFields.lastName !== true || lastNameValidationMessage == null}
                            validationMessage={lastNameValidationMessage ?? undefined}
                            onChange={event => setLastName(String(event.value ?? ""))}
                            onBlur={() => touchField("lastName")}
                        />
                        {touchedFields.lastName === true && lastNameValidationMessage != null && (
                            <div className="text-danger small">{lastNameValidationMessage}</div>
                        )}
                        <Input
                            id="email"
                            name="email"
                            label="Email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            valid={
                                touchedFields.email !== true || emailValidationMessage == null
                            }
                            validationMessage={emailValidationMessage ?? undefined}
                            onChange={event => setEmail(String(event.value ?? ""))}
                            onBlur={() => touchField("email")}
                        />
                        {touchedFields.email === true && emailValidationMessage != null && (
                            <div className="text-danger small">{emailValidationMessage}</div>
                        )}

                        <Input
                            id="phoneNumber"
                            name="phone"
                            label="Phone Number"
                            type="tel"
                            autoComplete="tel"
                            value={phoneNumber}
                            onChange={event => setPhoneNumber(String(event.value ?? ""))}
                        />

                        <Input
                            id="password"
                            name="newPassword"
                            label="Password"
                            type="password"
                            autoComplete="new-password"
                            value={password}
                            valid={
                                touchedFields.password !== true
                                || passwordValidationMessage == null
                            }
                            validationMessage={passwordValidationMessage ?? undefined}
                            onChange={event => setPassword(String(event.value ?? ""))}
                            onBlur={() => touchField("password")}
                        />
                        <div
                            className={
                                touchedFields.password === true
                                && passwordValidationMessage != null
                                    ? "text-danger small"
                                    : "text-muted small"
                            }
                        >
                            {touchedFields.password === true
                                && passwordValidationMessage != null
                                ? passwordValidationMessage
                                : passwordRequirements}
                        </div>

                        <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            label="Confirm Password"
                            type="password"
                            autoComplete="new-password"
                            value={confirmPassword}
                            valid={
                                touchedFields.confirmPassword !== true
                                || confirmPasswordValidationMessage == null
                            }
                            validationMessage={confirmPasswordValidationMessage ?? undefined}
                            onChange={event =>
                                setConfirmPassword(String(event.value ?? ""))}
                            onBlur={() => touchField("confirmPassword")}
                        />
                        {touchedFields.confirmPassword === true
                            && confirmPasswordValidationMessage != null && (
                            <div className="text-danger small">
                                {confirmPasswordValidationMessage}
                            </div>
                        )}
                        <MultiSelect
                            label="Roles"
                            data={roles}
                            value={selectedRoles}
                            valid={
                                touchedFields.roles !== true || rolesValidationMessage == null
                            }
                            validationMessage={rolesValidationMessage ?? undefined}
                            onChange={event => setSelectedRoles(event.value)}
                            onBlur={() => touchField("roles")}
                        />
                        {touchedFields.roles === true && rolesValidationMessage != null && (
                            <div className="text-danger small">{rolesValidationMessage}</div>
                        )}
                        <DropDownList
                            label="Customer Assignment"
                            data={customerModes}
                            value={customerMode}
                            onChange={event => {
                                setCustomerMode(event.value);
                                setTouchedFields(previous => ({
                                    ...previous,
                                    customer: false
                                }));
                            }}
                        />
                        {customerInput}
                    </div>
                    <DialogActionsBar>
                        <Button
                            type="button"
                            onClick={() => setIsCreateDialogOpen(false)}
                            disabled={isCreating}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            themeColor="primary"
                            disabled={!canCreate || isCreating}
                            onClick={() => void handleCreateAsync()}
                        >
                            Create
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
};

export default UserManagement;
